/* ============================================================
   SELLING-PLAN QUANTITY GUARD
   ============================================================
   Stops a shopper buying the deep quarterly per-unit price without the quarterly
   quantity. Measured on the live store, 23 Aug 2026, Collagen Greens at £55:

       1 pack every month       Product Quantity=1   14.54%   £47/pack
       3 packs every 3 months   Product Quantity=3   27.27%   £40/pack

   Shopify selling plans have NO minimum-quantity concept — the adjustment is a
   flat per-unit percentage — so dropping the quarterly line to 1 bought a single
   pack at £40, £7 under the monthly plan's own single pack. Recharge's
   "Product Quantity" option records the intended quantity but nothing enforces
   it. This enforces it, by moving the line to the best plan it still qualifies
   for rather than by blocking anything.

   WHY IT INTERCEPTS THE REQUEST RATHER THAN THE BUTTON
   ---------------------------------------------------
   Every quantity change on both surfaces — the drawer's -/+ buttons, typing into
   the number field, the /cart page, the remove button — funnels through a single
   POST to `/cart/change.js`. Patching there covers all of them at one seam and
   in ONE round trip: the corrected `selling_plan` rides along with the quantity,
   so the shopper never sees the £40 price flash before it is corrected.

   The alternative — listening for cart:update and issuing a second corrective
   request — was rejected for exactly that flash, and because it would fight
   Dawn's own re-render.

   Deliberately NOT a general fetch shim. It matches `/cart/change` and nothing
   else, and any request it does not understand is passed straight through
   untouched. Every failure path here falls back to the original request, so the
   worst case is the behaviour that existed before this file.

   IT ONLY EVER DOWNGRADES
   -----------------------
   Raising a monthly line from 1 to 3 does NOT move it to the quarterly plan.
   That would change the shopper's delivery frequency and their card charge
   without being asked, on a subscription they chose deliberately. The guard acts
   only when the CURRENT plan's own stated requirement is broken.
   ============================================================ */

(function () {
  'use strict';

  /* `0` means "remove this line" and must pass through untouched — a removal is
     not a quantity that needs a plan. */
  var REMOVE = 0;

  function readRules(row) {
    if (!row) return null;
    var raw = row.getAttribute('data-lg-plans');
    if (!raw) return null;
    try {
      var rules = JSON.parse(raw);
      return Array.isArray(rules) && rules.length ? rules : null;
    } catch (e) {
      /* Malformed JSON is a Liquid bug, not a shopper problem. Say so once in
         the console and let the request through unchanged. */
      console.warn('[lg-plan-guard] could not parse data-lg-plans', e);
      return null;
    }
  }

  /* The row for a change request. Dawn sends a 1-based `line`; the cart API also
     accepts `id` (the line key), which other callers may use, so both are
     resolved rather than assuming Dawn is the only client. */
  function findRow(body) {
    var input = null;
    if (body.line != null) {
      input =
        document.getElementById('Drawer-quantity-' + body.line) ||
        document.getElementById('Quantity-' + body.line);
    } else if (body.id != null) {
      input = document.querySelector('[data-quantity-line-key="' + String(body.id).replace(/"/g, '\\"') + '"]');
    }
    return input ? input.closest('.cart-item') : null;
  }

  /* The best plan the line still qualifies for: the highest minimum at or below
     the new quantity. If nothing qualifies — a plan set where every option is
     2 or more, and the shopper went to 1 — the lowest minimum is the closest
     honest offer, and the caller still checks that it differs from the current
     plan before acting. */
  function bestPlanFor(rules, quantity) {
    var eligible = rules.filter(function (r) {
      return typeof r.min === 'number' && r.min <= quantity;
    });
    var pool = eligible.length ? eligible : rules.slice();
    return pool.sort(function (a, b) {
      return eligible.length ? b.min - a.min : a.min - b.min;
    })[0];
  }

  /* Returns the plan to switch to, or null to leave the request alone. */
  function resolveSwap(body) {
    if (typeof body.quantity !== 'number' || body.quantity <= REMOVE) return null;

    var row = findRow(body);
    if (!row) return null;

    var currentId = row.getAttribute('data-lg-plan');
    if (!currentId) return null; /* one-time purchase — no plan to violate */

    var rules = readRules(row);
    if (!rules) return null;

    var current = rules.filter(function (r) {
      return String(r.id) === String(currentId);
    })[0];
    if (!current || body.quantity >= current.min) return null;

    var next = bestPlanFor(rules, body.quantity);
    if (!next || String(next.id) === String(currentId)) return null;

    return { from: current, to: next, quantity: body.quantity };
  }

  var toastTimer = null;

  /* Appended to <body>, not to the drawer. Every container inside the drawer has
     its innerHTML replaced by the re-render that this very change triggers, so a
     notice placed in one would be wiped a few hundred milliseconds after being
     written. The body is the only node both surfaces leave alone. */
  function announce(swap) {
    var el = document.getElementById('lg-plan-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'lg-plan-toast';
      el.className = 'lg-plan-toast';
      el.setAttribute('role', 'status');
      document.body.appendChild(el);
    }
    el.textContent =
      'Switched to ' + swap.to.name + ' — ' + swap.from.name + ' needs ' + swap.from.min + ' or more.';
    /* Reflow between removing and re-adding the class, so a second swap re-runs
       the entrance transition instead of sitting there already-shown. */
    el.classList.remove('is-up');
    void el.offsetWidth;
    el.classList.add('is-up');

    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      el.classList.remove('is-up');
    }, 6000);
  }

  var nativeFetch = window.fetch.bind(window);

  window.fetch = function (resource, init) {
    var url = typeof resource === 'string' ? resource : resource && resource.url;
    if (!url || url.indexOf('/cart/change') === -1 || !init || typeof init.body !== 'string') {
      return nativeFetch(resource, init);
    }

    var body;
    try {
      body = JSON.parse(init.body);
    } catch (e) {
      return nativeFetch(resource, init); /* not JSON — not ours */
    }

    var swap;
    try {
      swap = resolveSwap(body);
    } catch (e) {
      console.warn('[lg-plan-guard] skipped', e);
      return nativeFetch(resource, init);
    }
    if (!swap) return nativeFetch(resource, init);

    body.selling_plan = swap.to.id;
    var patched = Object.assign({}, init, { body: JSON.stringify(body) });

    return nativeFetch(resource, patched).then(function (response) {
      /* Announce only on success. A rejected change leaves the line on the plan
         it was already on, and saying otherwise would be a lie the shopper can
         see in the very next line of the basket. */
      if (response.ok) announce(swap);
      return response;
    });
  };
})();
